<script setup lang="ts">
import { PUBLIC_NAVIGATION_DESTINATIONS } from '~/shared-kernel/presentation/navigation/public-navigation'
import {
  CUSTOMER_MOBILE_DESTINATIONS,
  customerNavigationTarget,
  isCustomerNavigationActive
} from '~/shared-kernel/presentation/navigation/customer-navigation'

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const { $pwaInstall: installation } = useNuxtApp()
const eventsDestination = PUBLIC_NAVIGATION_DESTINATIONS[1]
const eventsPath = computed(() => localePath(eventsDestination.path))
const eventsActive = computed(() => route.path === eventsPath.value || route.path.startsWith(`${eventsPath.value}/`))
const mobileNavigationReady = useState<boolean>('customer-mobile-install-host', () => false)
// A body:has() rule would restyle the whole page on every DOM mutation; a class set while mounted does not.
useHead({ bodyAttrs: { class: 'has-customer-mobile-navigation' } })
let disposed = false
onMounted(async () => {
  await nextTick()
  if (!disposed) mobileNavigationReady.value = true
})
onBeforeUnmount(() => {
  disposed = true
  mobileNavigationReady.value = false
})

function revealActiveDestination(destination: typeof CUSTOMER_MOBILE_DESTINATIONS[number], event: MouseEvent) {
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return
  if (isCustomerNavigationActive(route, destination, localePath)) window.scrollTo({ top: 0, behavior: 'auto' })
}
</script>

<template>
  <Teleport to="#teleports">
    <nav class="customer-mobile-navigation" :aria-label="t('customer.navigation.quickAccess')">
      <div class="customer-mobile-navigation__item">
        <NuxtLink :to="eventsPath" :aria-current="eventsActive ? 'page' : undefined">
          <span class="customer-mobile-navigation__icon"><UIcon :name="eventsDestination.icon" aria-hidden="true" /></span>
          <span class="customer-mobile-navigation__label">{{ t(eventsDestination.labelKey) }}</span>
        </NuxtLink>
      </div>
      <div
        v-for="destination in CUSTOMER_MOBILE_DESTINATIONS"
        :key="destination.id"
        class="customer-mobile-navigation__item"
        :class="{ 'customer-mobile-navigation__item--primary': destination.id === 'rights' }"
      >
        <!-- The header avatar already opens the account, so installation borrows this slot. -->
        <span v-if="destination.id === 'account'" id="customer-mobile-install" />
        <NuxtLink
          v-if="destination.id !== 'account' || !installation?.visible.value"
          :to="customerNavigationTarget(destination, localePath)"
          :aria-current="isCustomerNavigationActive(route, destination, localePath) ? 'page' : undefined"
          @click="revealActiveDestination(destination, $event)"
        >
          <span class="customer-mobile-navigation__icon"><UIcon :name="destination.icon" aria-hidden="true" /></span>
          <span class="customer-mobile-navigation__label">{{ t(destination.labelKey) }}</span>
        </NuxtLink>
      </div>
    </nav>
  </Teleport>
</template>

<style scoped>
.customer-mobile-navigation { display: none; }
@media (max-width: 42rem) {
  /* Symmetric widths keep the raised wallet centred while giving the longest labels more room. */
  .customer-mobile-navigation { position: fixed; z-index: 30; right: 0; bottom: 0; left: 0; display: grid; grid-template-columns: .9fr 1.15fr 1fr 1.15fr .9fr; align-items: end; border-top: 1px solid var(--tq-border); background: var(--tq-bg-elevated); padding: .3rem .2rem calc(.3rem + env(safe-area-inset-bottom)); }
  .customer-mobile-navigation__item { min-width: 0; }
  .customer-mobile-navigation a { display: flex; flex-direction: column; align-items: center; gap: .2rem; min-height: 3.25rem; border-radius: var(--tq-radius-md); padding: .15rem 0; color: var(--tq-text-muted); font-size: .66rem; font-weight: 500; letter-spacing: -.02em; line-height: 1.15; text-align: center; text-decoration: none; }
  .customer-mobile-navigation a:focus-visible { outline: 2px solid var(--tq-focus); outline-offset: 2px; }
  .customer-mobile-navigation__icon { display: grid; place-items: center; width: min(3.25rem, 100%); height: 1.9rem; border-radius: var(--tq-radius-pill); font-size: 1.25rem; }
  .customer-mobile-navigation__label { max-width: 100%; hyphens: auto; overflow-wrap: break-word; }
  .customer-mobile-navigation a[aria-current] { color: var(--tq-accent-readable); font-weight: 600; }
  .customer-mobile-navigation a[aria-current] .customer-mobile-navigation__icon { background: var(--tq-accent-chip-bg); }
  /* The wallet is the reason customers open the app, so its slot is raised and filled. */
  .customer-mobile-navigation__item--primary a { color: var(--tq-text); }
  .customer-mobile-navigation__item--primary .customer-mobile-navigation__icon,
  .customer-mobile-navigation__item--primary a[aria-current] .customer-mobile-navigation__icon { width: 3.4rem; height: 3.4rem; margin-top: -1.5rem; border-radius: 50%; background: var(--tq-action); background-image: var(--tq-gradient-action); color: var(--tq-on-action); font-size: 1.5rem; box-shadow: 0 0 0 .3rem var(--tq-bg-elevated), var(--tq-shadow-tight); }
}
</style>
